/**
 * "Showing X-Y of Z" + page-size selector — used by all compact lists.
 *
 * `page` is 1-indexed. Parent owns the page state; this component is
 * fully controlled. `pageSize` defaults to 25 / 50 / 100 (matches spec).
 */
import React from 'react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const Pagination = ({ page, pageSize, total, onPageChange, onPageSizeChange, testid = 'pagination' }) => {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="flex items-center justify-between gap-3 mt-3 flex-wrap" data-testid={testid}>
      <div className="flex items-center gap-3 text-xs text-slate-500">
        <span data-testid={`${testid}-range`}>Showing {from}-{to} of {total}</span>
        <div className="h-3 w-px bg-slate-200" />
        <div className="flex items-center gap-2">
          <span>Rows</span>
          <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
            <SelectTrigger className="h-7 w-[64px] text-xs" data-testid={`${testid}-size-select`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <PageBtn onClick={() => onPageChange(Math.max(1, page - 1))} disabled={page <= 1} testid={`${testid}-prev`}>
          Prev
        </PageBtn>
        <span className="text-xs text-slate-500 px-2" data-testid={`${testid}-current`}>
          Page {page} / {lastPage}
        </span>
        <PageBtn onClick={() => onPageChange(Math.min(lastPage, page + 1))} disabled={page >= lastPage} testid={`${testid}-next`}>
          Next
        </PageBtn>
      </div>
    </div>
  );
};

const PageBtn = ({ onClick, disabled, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    className="text-xs px-2.5 py-1 rounded border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
  >
    {children}
  </button>
);

export default Pagination;
